/**
 * Peças dos formulários de cadastro/edição (visual v2): cabeçalho da página,
 * seção em cartão e campo com rótulo.
 */
import type { ComponentType, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { cn } from '@/utils/cn'

interface FormPageHeaderProps {
  title: string
  description?: string
  icon: ComponentType<{ className?: string }>
  backTo: string
  backLabel: string
}

export function FormPageHeader({ title, description, icon: Icon, backTo, backLabel }: FormPageHeaderProps) {
  return (
    <div className="flex flex-col gap-2">
      <Button variant="ghost" size="sm" asChild className="-ml-2 self-start">
        <Link to={backTo}><ArrowLeft className="size-4" /> {backLabel}</Link>
      </Button>
      <div className="flex items-center gap-2">
        <Icon className="size-6 shrink-0 text-muted-foreground" />
        <h1 className="min-w-0 break-words text-2xl font-semibold">{title}</h1>
      </div>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
    </div>
  )
}

interface FormSectionProps {
  title: string
  icon?: ComponentType<{ className?: string }>
  description?: string
  className?: string
  children: ReactNode
}

export function FormSection({ title, icon: Icon, description, className, children }: FormSectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {Icon && <Icon className="size-4 text-muted-foreground" aria-hidden="true" />}
          <h2>{title}</h2>
        </CardTitle>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </CardHeader>
      <CardContent className={cn('grid grid-cols-1 gap-4', className)}>{children}</CardContent>
    </Card>
  )
}

interface FormFieldProps {
  label: ReactNode
  htmlFor?: string
  hint?: ReactNode
  error?: string
  className?: string
  children: ReactNode
}

export function FormField({ label, htmlFor, hint, error, className, children }: FormFieldProps) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

/** Faixa de ações no fim do formulário (Cancelar / Salvar). */
export function FormActions({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap justify-end gap-2">{children}</div>
}
